import { ArrowPathIcon, CheckIcon } from "@heroicons/react/24/outline";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { ReactNode } from "react";
import { useFormState } from "react-hook-form";

import { Button } from "@fronterahealth/frontera-ui-components/ui/button";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { isSuggestedEdit } from "@components/QaReview/PeerReview/peerReview.suggestedEdit";
import { CommentActionTooltip } from "@components/QaReview/components/CommentActionTooltip";
import { useApplyComment } from "@components/QaReview/hooks/useApplyComment";
import { useResolveItem } from "@components/QaReview/hooks/useResolveItem";
import { useReviewFields } from "@components/QaReview/hooks/useReviewFields";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";

const QA_RESOLVE_LABEL = "Resolve";
const COMMENT_RESOLVE_LABEL = "Mark as resolved and hide discussion";
const ACCEPT_LABEL = "Accept and resolve";

/** What the checkmark does for one open card. The card and the navigator draw it their own way. */
export interface CheckActionState {
  kind: "accept" | "resolve";
  label: string;
  /** The old button's text, so RUM click history stays continuous. */
  actionName: "Apply" | "Resolve";
  run: () => void;
  disabled: boolean;
  isPending: boolean;
  /** Why the action is disabled, when the label alone wouldn't say. */
  title?: string;
}

interface CheckActionProps {
  comment: ReviewItemFieldsFragment;
  /** Keep the page where it is instead of moving to the next open card. */
  stayPut?: boolean;
  /** Gets null for a resolved card, which has nothing to accept or resolve. */
  children: (action: CheckActionState | null) => ReactNode;
}

const AcceptAction: React.FC<CheckActionProps & { fieldId: string }> = ({ comment, fieldId, stayPut, children }) => {
  const { requestApply } = useApplyComment({ advance: !stayPut });
  const { dirtyFields } = useFormState({ name: fieldId });
  // The edit is written over the field, so wait until the author's own typing has saved.
  const hasUnsavedChanges = !!dirtyFields[fieldId];
  const isApplying = useReviewSession().state.applyingFieldId !== null;

  return children({
    kind: "accept",
    label: ACCEPT_LABEL,
    actionName: "Apply",
    run: () => requestApply(comment),
    disabled: hasUnsavedChanges || isApplying,
    isPending: false,
    title: hasUnsavedChanges ? "Waiting for your latest changes to save" : undefined,
  });
};

const ResolveAction: React.FC<CheckActionProps> = ({ comment, stayPut, children }) => {
  const { resolve, isResolving } = useResolveItem({ advance: !stayPut });
  const isPending = isResolving(comment);

  return children({
    kind: "resolve",
    label: comment.__typename === "ReportCommentType" ? COMMENT_RESOLVE_LABEL : QA_RESOLVE_LABEL,
    actionName: "Resolve",
    run: () => resolve(comment),
    disabled: isPending,
    isPending,
  });
};

/** Accepts a Team Review suggested edit, otherwise resolves the card. */
export const CheckAction: React.FC<CheckActionProps> = ({ comment, stayPut, children }) => {
  const fieldsById = useReviewFields();
  if (isReviewItemResolved(comment)) return children(null);

  const fieldId = comment.reportSectionField?.id;

  // A suggested edit can only be applied to a field this report renders (useApplyComment looks it up
  // the same way); on a removed or excluded field the card can still be resolved.
  if (fieldId && fieldsById[fieldId] && isSuggestedEdit(comment)) {
    return (
      <AcceptAction comment={comment} fieldId={fieldId} stayPut={stayPut}>
        {children}
      </AcceptAction>
    );
  }
  return (
    <ResolveAction comment={comment} stayPut={stayPut}>
      {children}
    </ResolveAction>
  );
};

// Icon only, so the tooltip's words are also its accessible name.
const CheckButton: React.FC<{ action: CheckActionState }> = ({ action }) => (
  <CommentActionTooltip label={action.label}>
    <Button
      variant="ghost"
      size="icon-sm"
      className="rounded-full text-dynamic-stroke-success-strong-default hover:bg-static-fill-alpha-base hover:text-dynamic-stroke-success-strong-hover"
      aria-label={action.label}
      data-dd-action-name={action.actionName}
      title={action.title}
      onClick={(e) => {
        e.stopPropagation();
        action.run();
      }}
      disabled={action.disabled}
    >
      {action.isPending ? <ArrowPathIcon className="size-4 animate-spin" /> : <CheckIcon className="size-4 stroke-2" />}
    </Button>
  </CommentActionTooltip>
);

/** The card's one checkmark, in its header. */
export const CommentActionResolve: React.FC<{ comment: ReviewItemFieldsFragment }> = ({ comment }) => (
  <CheckAction comment={comment}>{(action) => action && <CheckButton action={action} />}</CheckAction>
);
